import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { 
  Box, 
  Fab, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  WaterDrop as WaterDropIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';
import {
  addChartToSite,
  deleteChartFromSite,
  updateChartInSite,
  logActivity,
  getTelemetryHistory,
  getTelemetryData,
} from '../api/apiService';
import { useSelector } from 'react-redux';
import { TELEMETRY_LIVE_POLL_MS } from '../constants/telemetry';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.06)',
  backgroundColor: theme.palette.background.paper,
  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 24px rgba(0,0,0,0.10)',
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'
  }
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
}));

const KpiSparklineAnimated = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'lineColor',
})(({ theme, lineColor }) => {
  const primary = lineColor || theme.palette.brand?.royalBlue || theme.palette.primary.main;
  return {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    '& .recharts-surface': {
      overflow: 'visible',
    },
    '& .recharts-area-curve': {
      strokeDasharray: 1600,
      animation: 'kpiTrendLineDraw 2s ease-in-out forwards, kpiTrendLinePulse 2.8s ease-in-out 2s infinite',
    },
    '& .recharts-area-area': {
      transformOrigin: 'bottom center',
      animation: 'kpiTrendFillRise 1.6s ease-out forwards',
    },
    '@keyframes kpiTrendLineDraw': {
      from: { strokeDashoffset: 1600 },
      to: { strokeDashoffset: 0 },
    },
    '@keyframes kpiTrendFillRise': {
      from: { opacity: 0, transform: 'scaleY(0.55)' },
      to: { opacity: 1, transform: 'scaleY(1)' },
    },
    '@keyframes kpiTrendLinePulse': {
      '0%, 100%': {
        filter: `drop-shadow(0 0 2px ${alpha(primary, 0.25)})`,
      },
      '50%': {
        filter: `drop-shadow(0 0 8px ${alpha(primary, 0.5)})`,
      },
    },
    '& .kpi-wave-track': {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 4,
      height: 32,
      pointerEvents: 'none',
      opacity: theme.palette.mode === 'dark' ? 0.35 : 0.45,
    },
    '& .kpi-wave-path': {
      animation: 'kpiWaveDrift 3.5s ease-in-out infinite',
      transformOrigin: 'center',
    },
    '@keyframes kpiWaveDrift': {
      '0%, 100%': { transform: 'translateX(0) scaleY(1)' },
      '50%': { transform: 'translateX(-6px) scaleY(1.08)' },
    },
  };
});

const mockPieData = [
  { name: 'Operational', value: 75 },
  { name: 'Maintenance', value: 15 },
  { name: 'Downtime', value: 10 }
];

const COLORS = (theme) => [
  theme.palette.chart?.series1 || theme.palette.primary.main,
  theme.palette.chart?.series2 || theme.palette.success.main,
  theme.palette.chart?.series3 || theme.palette.warning.main,
  theme.palette.chart?.series4 || theme.palette.error.main,
  theme.palette.chart?.series5 || theme.palette.brand?.electricViolet || '#7A5AF5',
];

const parseNumericValue = (value) => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseTelemetryHistory = (json) => {
  const items = Array.isArray(json?.data) ? json.data : [];
  const dataPoints = items
    .map((item) => ({
      time: item.localTime || String(item.timestamp || ''),
      value: parseNumericValue(item.value),
    }))
    .filter((point) => point.value !== null);
  const currentValue = dataPoints.length ? dataPoints[dataPoints.length - 1].value : null;
  return { dataPoints, currentValue };
};

const getSparklineYDomain = (dataPoints) => {
  if (!dataPoints?.length) return [0, 100];
  const values = dataPoints.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    const pad = Math.max(Math.abs(min) * 0.02, 1);
    return [min - pad, max + pad];
  }
  const range = max - min;
  const padding = Math.max(range * 0.12, 0.5);
  return [min - padding, max + padding];
};

/** Interpolate extra points so the sparkline draws a smooth, wavy curve through real readings */
const buildWavySparklineData = (points, stepsPerSegment = 5) => {
  if (!points?.length) return [];
  if (points.length === 1) return points;

  const dense = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    dense.push(a);
    for (let s = 1; s < stepsPerSegment; s++) {
      const t = s / stepsPerSegment;
      const ease = t * t * (3 - 2 * t);
      dense.push({
        time: a.time,
        value: a.value + (b.value - a.value) * ease,
      });
    }
  }
  dense.push(points[points.length - 1]);
  return dense;
};

const getYDomain = (dataPoints) => {
  if (!dataPoints?.length) return [0, 100];
  const values = dataPoints.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [min - 1, max + 1];
  const padding = (max - min) * 0.1 || 1;
  return [min - padding, max + padding];
};

const chartIdsMatch = (a, b) => String(a) === String(b);

const toChartPersistencePayload = (chart) => ({
  id: chart.id,
  chartName: chart.chartName,
  chartType: chart.chartType,
  selectedInstrument: chart.selectedInstrument,
  hours: chart.hours ?? 1,
});

const CHART_TYPE_OPTIONS = [
  { value: 'KPIChart', label: 'KPI' },
  { value: 'LineChart', label: 'Line Chart' },
  { value: 'BarChart', label: 'Bar Chart' },
  { value: 'AreaChart', label: 'Area Chart' },
  { value: 'PieChart', label: 'Pie Chart' },
];

const formatKpiValue = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  if (Math.abs(num) >= 1000) return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return num.toFixed(2);
};

/** Recharts line/area need 2+ points to draw; pad a single last reading so the graph shows immediately */
const ensureChartSeriesData = (dataPoints) => {
  if (!dataPoints?.length) return [];
  if (dataPoints.length >= 2) return dataPoints;
  const last = dataPoints[dataPoints.length - 1];
  return [
    { ...last, time: last.time || 'Previous' },
    { ...last, time: last.time ? `${last.time} (latest)` : 'Latest' },
  ];
};

const findLiveItem = (liveByTagName, tagName) => {
  if (!tagName || !liveByTagName) return null;
  if (liveByTagName[tagName]) return liveByTagName[tagName];
  const normalized = tagName.trim().toLowerCase();
  const key = Object.keys(liveByTagName).find((k) => k.trim().toLowerCase() === normalized);
  return key ? liveByTagName[key] : null;
};

const buildLiveLookup = (liveData) => {
  const liveByTagName = {};
  (liveData || []).forEach((item) => {
    if (item.tagName) liveByTagName[item.tagName] = item;
    if (item.tagId?.includes(':')) {
      liveByTagName[item.tagId.split(':').pop()] = item;
    }
  });
  return liveByTagName;
};

/** Line/bar/area show as soon as live or history data exists */
const isChartReadyToRender = (chart) => {
  if (chart.chartType === 'KPIChart') {
    return chart.currentValue != null;
  }
  if (chart.chartType === 'PieChart') {
    return true;
  }
  return (chart.dataPoints?.length ?? 0) > 0 || chart.currentValue != null;
};

const shouldShowChartSkeleton = (chart) =>
  chart.isLoadingData || !isChartReadyToRender(chart);

/** Cap history points so DynamoDB queries return faster */
const HISTORY_MAX_READINGS = 80;
/** Avoid very wide history windows that time out the legacy Lambda */
const HISTORY_HOURS_CAP = 24;
/** Don't fire all chart history requests at once (causes 504s) */
const HISTORY_FETCH_CONCURRENCY = 2;

const liveItemToPoint = (item) => {
  const value = parseNumericValue(item.value);
  if (value === null) return null;
  return {
    value,
    point: {
      time: item.localTime || item.deviceLocalTime || new Date().toLocaleString(),
      value,
    },
  };
};

const ChartDataSkeleton = ({ chartType }) => (
  <Box sx={{ width: '100%', py: 1 }}>
    {chartType === 'KPIChart' ? (
      <Skeleton variant="rounded" width="100%" height={200} sx={{ borderRadius: 3 }} />
    ) : (
      <Skeleton variant="rounded" width="100%" height={300} sx={{ borderRadius: 2 }} />
    )}
  </Box>
);

const ChartCardSkeleton = () => (
  <StyledCard>
    <ChartHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="text" width="50%" height={28} sx={{ borderRadius: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="circular" width={32} height={32} />
        ))}
      </Box>
    </ChartHeader>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
      <Skeleton variant="text" width={140} height={44} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rounded" width="100%" height={300} sx={{ borderRadius: 2 }} />
    </CardContent>
  </StyledCard>
);

const VisualizationTab = ({ projectId, tagNames, siteData }) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);
  const [chartDetails, setChartDetails] = useState({
    chartName: '',
    chartType: '',
    selectedInstrument: '',
    hours: 1,
  });
  const [charts, setCharts] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [savingChart, setSavingChart] = useState(false);
  const [saveError, setSaveError] = useState('');
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const historyCacheRef = useRef(new Map());

  const chartsConfigKey = useMemo(
    () =>
      JSON.stringify(
        (siteData?.charts || []).map((c) => ({
          id: c.id,
          chartType: c.chartType,
          selectedInstrument: c.selectedInstrument,
          hours: c.hours,
        }))
      ),
    [siteData?.charts]
  );

  const instrumentNames = useMemo(() => {
    const fromSite = (siteData?.instruments || [])
      .map((instrument) => instrument.tagNo || instrument.selectInstrument)
      .filter(Boolean);
    const fromTelemetry = tagNames || [];
    return [...new Set([...fromSite, ...fromTelemetry])].sort();
  }, [siteData?.instruments, tagNames]);

  const formatTagId = useCallback((tagNo) => {
    const serverName = siteData?.serverName || 'PLC1';
    const cleanServerName = serverName.replace(`${projectId}_`, '');
    return `${projectId}_${cleanServerName}:${tagNo}`;
  }, [projectId, siteData?.serverName]);

  const applyLiveDataToAllCharts = useCallback((chartList, liveData) => {
    const liveByTagName = buildLiveLookup(liveData);
    return chartList.map((chart) => {
      if (!chart.selectedInstrument || chart.selectedInstrument === 'System Status') {
        return { ...chart, isLoadingData: false };
      }

      const item = findLiveItem(liveByTagName, chart.selectedInstrument);
      const live = item ? liveItemToPoint(item) : null;
      if (!live) return chart;

      return {
        ...chart,
        currentValue: live.value,
        dataPoints: [live.point],
        isLiveOnly: true,
        isLoadingData: false,
      };
    });
  }, []);

  const fetchHistoryData = useCallback(async (tagName, hours = 1) => {
    if (!projectId || !tagName) {
      return { dataPoints: [], currentValue: null };
    }

    const safeHours = Math.min(Math.max(1, Number(hours) || 1), HISTORY_HOURS_CAP);
    const historyParams = {
      projectId,
      hours: String(safeHours),
      tagName,
      maxReadings: String(HISTORY_MAX_READINGS),
      // Do not send serverName — FilterExpression on legacy history API slows queries into 504s
    };

    const cacheKey = `${projectId}|${tagName}|${safeHours}`;
    let historyPromise = historyCacheRef.current.get(cacheKey);
    if (!historyPromise) {
      historyPromise = getTelemetryHistory(historyParams)
        .then((json) => parseTelemetryHistory(json))
        .catch((error) => {
          console.warn('[VisualizationTab] History fetch failed:', historyParams, error);
          return { dataPoints: [], currentValue: null };
        });
      historyCacheRef.current.set(cacheKey, historyPromise);
    }

    return historyPromise;
  }, [projectId]);

  const fetchLiveForCharts = useCallback(async (chartList) => {
    const tagIds = [...new Set(
      chartList
        .map((c) => c.selectedInstrument)
        .filter(Boolean)
        .map((tag) => formatTagId(tag))
    )];

    if (!tagIds.length) return [];

    let liveData = await getTelemetryData(projectId, tagIds);

    if (!liveData?.length) {
      const wanted = new Set(
        chartList
          .map((c) => c.selectedInstrument?.trim().toLowerCase())
          .filter(Boolean)
      );
      const allLive = await getTelemetryData(projectId);
      liveData = (allLive || []).filter((item) => {
        const tag = item.tagName?.trim().toLowerCase();
        const fromId = item.tagId?.split(':').pop()?.trim().toLowerCase();
        return wanted.has(tag) || wanted.has(fromId);
      });
    }

    return liveData || [];
  }, [projectId, formatTagId]);

  const updateChartInState = useCallback((chartId, patch) => {
    setCharts((prev) =>
      prev.map((c) => (chartIdsMatch(c.id, chartId) ? { ...c, ...patch } : c))
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const hydrateChartHistory = async (chart) => {
      try {
        const { dataPoints, currentValue } = await fetchHistoryData(
          chart.selectedInstrument,
          chart.hours || 1
        );
        if (isCancelled()) return;
        if (dataPoints.length === 0) {
          updateChartInState(chart.id, { isLoadingData: false });
          return;
        }
        updateChartInState(chart.id, {
          dataPoints,
          currentValue: currentValue ?? dataPoints[dataPoints.length - 1]?.value,
          isLiveOnly: false,
          isLoadingData: false,
        });
      } catch (error) {
        console.error('[VisualizationTab] History hydrate failed:', chart.id, error);
        if (!isCancelled()) updateChartInState(chart.id, { isLoadingData: false });
      }
    };

    const loadCharts = async () => {
      if (!projectId) return;

      if (!siteData) {
        setLoadingCharts(true);
        return;
      }

      try {
        historyCacheRef.current.clear();

        const savedCharts = (siteData.charts || []).map((chart) => ({
          ...chart,
          isLoadingData: true,
        }));

        if (isCancelled()) return;
        setCharts(savedCharts);
        setLoadingCharts(false);

        if (!savedCharts.length) return;

        const liveData = await fetchLiveForCharts(savedCharts);
        if (!isCancelled() && liveData.length) {
          setCharts((prev) => applyLiveDataToAllCharts(prev, liveData));
        } else if (!isCancelled()) {
          setCharts((prev) =>
            prev.map((c) =>
              c.isLoadingData &&
              c.selectedInstrument &&
              c.selectedInstrument !== 'System Status'
                ? { ...c, isLoadingData: false }
                : c
            )
          );
        }

        // Hydrate history with limited concurrency (avoids flooding API → 504)
        const historyJobs = savedCharts.filter(
          (chart) => chart.selectedInstrument && chart.selectedInstrument !== 'System Status'
        );

        savedCharts.forEach((chart) => {
          if (!chart.selectedInstrument || chart.selectedInstrument === 'System Status') {
            if (!isCancelled()) updateChartInState(chart.id, { isLoadingData: false });
          }
        });

        for (let i = 0; i < historyJobs.length; i += HISTORY_FETCH_CONCURRENCY) {
          if (isCancelled()) break;
          const batch = historyJobs.slice(i, i + HISTORY_FETCH_CONCURRENCY);
          await Promise.all(batch.map((chart) => hydrateChartHistory(chart)));
        }
      } catch (error) {
        console.error('[VisualizationTab] Error fetching charts:', error);
      } finally {
        if (!isCancelled()) {
          setLoadingCharts(false);
        }
      }
    };

    loadCharts();
    return () => {
      cancelled = true;
    };
  }, [projectId, chartsConfigKey, siteData, fetchLiveForCharts, applyLiveDataToAllCharts, fetchHistoryData, updateChartInState]);

  // Poll live telemetry to keep charts updated (especially when history is unavailable)
  useEffect(() => {
    if (!projectId || charts.length === 0) return;

    const pollLiveUpdates = async () => {
      const tagIds = charts
        .filter((c) => c.selectedInstrument && c.selectedInstrument !== 'System Status')
        .map((c) => formatTagId(c.selectedInstrument));

      if (!tagIds.length) return;

      try {
        const liveData = await getTelemetryData(projectId, tagIds);
        const liveByTagName = buildLiveLookup(liveData);

        setCharts((prev) =>
          prev.map((chart) => {
            if (!chart.selectedInstrument || chart.selectedInstrument === 'System Status') return chart;
            const item = findLiveItem(liveByTagName, chart.selectedInstrument);
            if (!item) return chart;

            const value = parseNumericValue(item.value);
            if (value === null) return chart;

            const newPoint = {
              time: item.localTime || item.deviceLocalTime || new Date().toLocaleString(),
              value,
            };

            const existing = chart.dataPoints || [];
            const last = existing[existing.length - 1];
            const isDuplicate =
              last && last.value === newPoint.value && last.time === newPoint.time;
            const dataPoints = isDuplicate
              ? existing
              : [...existing, newPoint].slice(-50);

            return {
              ...chart,
              dataPoints,
              currentValue: value,
              isLiveOnly: chart.isLiveOnly === false ? false : true,
              isLoadingData: false,
            };
          })
        );
      } catch (error) {
        console.error('[VisualizationTab] Live poll failed:', error);
      }
    };

    pollLiveUpdates();
    const intervalId = setInterval(pollLiveUpdates, TELEMETRY_LIVE_POLL_MS);
    return () => clearInterval(intervalId);
    // charts.length: restart poll only when chart count changes, not on every live point
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, charts.length, formatTagId]);

  const handleClickOpen = () => {
    setSaveError('');
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setChartDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!chartDetails.chartName?.trim() || !chartDetails.chartType || !chartDetails.selectedInstrument) {
      setSaveError('Please fill in chart name, instrument, and chart type.');
      return;
    }

    setSavingChart(true);
    setSaveError('');

    const hours = Number(chartDetails.hours) || 1;
    const tempId = Date.now();
    const optimisticChart = {
      id: tempId,
      chartName: chartDetails.chartName.trim(),
      chartType: chartDetails.chartType,
      selectedInstrument: chartDetails.selectedInstrument,
      hours,
      dataPoints: [],
      currentValue: null,
      isLiveOnly: false,
      isLoadingData: true,
    };

    setCharts((prev) => [...prev, optimisticChart]);
    const instrument = chartDetails.selectedInstrument;
    setChartDetails({ chartName: '', chartType: '', selectedInstrument: '', hours: 1 });
    handleClose();

    try {
      const liveData = await fetchLiveForCharts([optimisticChart]);
      const withLive = applyLiveDataToAllCharts([optimisticChart], liveData)[0];

      await addChartToSite(projectId, toChartPersistencePayload(withLive));

      try {
        await logActivity(currentUser.email, `Created chart: ${withLive.chartName}`);
      } catch (err) {
        console.error('Error logging create chart activity to DynamoDB:', err);
      }

      setCharts((prevCharts) =>
        prevCharts.map((c) => (chartIdsMatch(c.id, tempId) ? withLive : c))
      );

      fetchHistoryData(instrument, hours).then(({ dataPoints, currentValue }) => {
        if (dataPoints.length === 0) return;
        updateChartInState(tempId, {
          dataPoints,
          currentValue,
          isLiveOnly: false,
          isLoadingData: false,
        });
      });
    } catch (error) {
      console.error('[VisualizationTab] Error saving chart:', error);
      setSaveError('Failed to save chart. Please try again.');
      setCharts((prev) => prev.filter((c) => !chartIdsMatch(c.id, tempId)));
    } finally {
      setSavingChart(false);
    }
  };

  const handleDeleteChart = async (chartId) => {
    const chartToDelete = charts.find((chart) => chart.id === chartId);
    try {
      await deleteChartFromSite(projectId, chartId);
      setCharts((prevCharts) => prevCharts.filter((chart) => !chartIdsMatch(chart.id, chartId)));
      try {
        await logActivity(currentUser.email, `Deleted chart: ${chartToDelete?.chartName}`);
      } catch (err) {
        console.error('Error logging delete chart activity to DynamoDB:', err);
      }
    } catch (error) {
      console.error('[VisualizationTab] Error deleting chart:', error);
    }
  };

  const handleEdit = (chart) => {
    setSelectedChart(chart);
    setChartDetails({
      chartName: chart.chartName,
      chartType: chart.chartType,
      selectedInstrument: chart.selectedInstrument,
      hours: chart.hours || 1,
    });
    setOpenEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedChart) return;

    const updatedMeta = {
      ...selectedChart,
      chartName: chartDetails.chartName || selectedChart.chartName,
      chartType: chartDetails.chartType || selectedChart.chartType,
      selectedInstrument: chartDetails.selectedInstrument || selectedChart.selectedInstrument,
      hours: Number(chartDetails.hours) || 1,
    };

    // Apply chart type and metadata immediately; keep skeleton until data is ready
    setCharts((prev) =>
      prev.map((c) =>
        chartIdsMatch(c.id, selectedChart.id)
          ? { ...c, ...updatedMeta, isLoadingData: true }
          : c
      )
    );

    try {
      if (userRole === 'Admin' || userRole === 'Manager') {
        await updateChartInSite(projectId, toChartPersistencePayload(updatedMeta));
      }

      const liveData = await fetchLiveForCharts([updatedMeta]);
      const withLive = applyLiveDataToAllCharts(
        [{ ...updatedMeta, isLoadingData: true }],
        liveData
      )[0];

      setCharts((prev) =>
        prev.map((c) => (chartIdsMatch(c.id, selectedChart.id) ? withLive : c))
      );
      setOpenEditDialog(false);
      setSelectedChart(null);

      fetchHistoryData(updatedMeta.selectedInstrument, updatedMeta.hours).then(
        ({ dataPoints, currentValue }) => {
          if (dataPoints.length === 0) return;
          updateChartInState(selectedChart.id, {
            dataPoints,
            currentValue,
            isLiveOnly: false,
            isLoadingData: false,
          });
        }
      );

      if (userRole === 'Admin' || userRole === 'Manager') {
        setChartDetails({ chartName: '', chartType: '', selectedInstrument: '', hours: 1 });
      } else {
        setChartDetails((prev) => ({ ...prev, hours: 1 }));
      }
    } catch (error) {
      console.error('Error updating chart:', error);
    }
  };

  const handleRefresh = async (chart) => {
    updateChartInState(chart.id, { isLoadingData: true });
    try {
      const liveData = await fetchLiveForCharts([chart]);
      const withLive = applyLiveDataToAllCharts([{ ...chart, isLoadingData: true }], liveData)[0];
      updateChartInState(chart.id, withLive);

      const { dataPoints, currentValue } = await fetchHistoryData(
        chart.selectedInstrument,
        chart.hours || 1
      );
      if (dataPoints.length > 0) {
        updateChartInState(chart.id, {
          dataPoints,
          currentValue,
          isLiveOnly: false,
          isLoadingData: false,
        });
      }
    } catch (error) {
      console.error('[VisualizationTab] Error refreshing chart data:', error);
      updateChartInState(chart.id, { isLoadingData: false });
    }
  };

  const renderKPIChart = (chart) => {
    const { dataPoints, currentValue } = chart;
    const primary = theme.palette.brand?.royalBlue || theme.palette.primary.main;
    const sparkData = (dataPoints || []).slice(-24);
    const wavySparkData = buildWavySparklineData(sparkData);
    const firstValue = sparkData[0]?.value;
    const lastValue = currentValue ?? sparkData[sparkData.length - 1]?.value;

    if (lastValue == null) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: 'center', fontStyle: 'italic' }}
        >
          No telemetry data for &quot;{chart.selectedInstrument}&quot; yet. Try Refresh.
        </Typography>
      );
    }
    const trendPct =
      sparkData.length >= 2 && firstValue !== 0
        ? ((lastValue - firstValue) / Math.abs(firstValue)) * 100
        : null;
    const isUp = trendPct != null && trendPct >= 0;
    const lastTime = sparkData[sparkData.length - 1]?.time;
    const gradientId = `kpi-grad-${chart.id}`;
    const sparkYDomain = getSparklineYDomain(wavySparkData.length ? wavySparkData : sparkData);

    return (
      <Box
        sx={{
          width: '100%',
          borderRadius: 3,
          p: { xs: 2.5, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
          background: (t) =>
            t.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${alpha(primary, 0.22)} 0%, ${alpha(primary, 0.04)} 55%, ${alpha(t.palette.background.paper, 0.9)} 100%)`
              : `linear-gradient(145deg, ${alpha(primary, 0.10)} 0%, ${alpha(primary, 0.03)} 55%, #ffffff 100%)`,
          border: (t) => `1px solid ${alpha(primary, t.palette.mode === 'dark' ? 0.28 : 0.14)}`,
          boxShadow: (t) =>
            t.palette.mode === 'dark'
              ? `0 12px 32px ${alpha(primary, 0.18)}`
              : `0 12px 28px ${alpha(primary, 0.10)}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -48,
            right: -48,
            width: 140,
            height: 140,
            borderRadius: '50%',
            bgcolor: alpha(primary, 0.08),
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
          <Box>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1.6, color: 'text.secondary', fontWeight: 600 }}
            >
              Live KPI
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontFeatureSettings: '"tnum"',
                color: primary,
                lineHeight: 1.05,
                fontSize: { xs: '2.25rem', sm: '2.75rem' },
              }}
            >
              {formatKpiValue(lastValue)}
            </Typography>
          </Box>
          <ShowChartIcon sx={{ color: alpha(primary, 0.45), fontSize: 32 }} />
        </Box>

        {trendPct != null && (
          <Chip
            size="small"
            icon={isUp ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={`${isUp ? '+' : ''}${trendPct.toFixed(1)}%`}
            sx={{
              mb: 2,
              fontWeight: 600,
              bgcolor: alpha(isUp ? theme.palette.success.main : theme.palette.error.main, 0.12),
              color: isUp ? theme.palette.success.main : theme.palette.error.main,
              border: 'none',
            }}
          />
        )}

        {sparkData.length > 1 ? (
          <KpiSparklineAnimated lineColor={primary} sx={{ height: 88, mt: 1 }}>
            <svg
              className="kpi-wave-track"
              width="100%"
              height="32"
              viewBox="0 0 400 32"
              preserveAspectRatio="none"
              aria-hidden
            >
              <path
                className="kpi-wave-path"
                d="M0,16 C25,8 50,24 75,16 S125,8 150,16 S200,24 225,16 S275,8 300,16 S350,24 375,16 L400,16"
                fill="none"
                stroke={primary}
                strokeWidth="1.5"
                strokeOpacity={0.22}
                strokeLinecap="round"
              >
                <animate
                  attributeName="d"
                  dur="4s"
                  repeatCount="indefinite"
                  values="
                    M0,16 C25,8 50,24 75,16 S125,8 150,16 S200,24 225,16 S275,8 300,16 S350,24 375,16 L400,16;
                    M0,16 C25,24 50,8 75,16 S125,24 150,16 S200,8 225,16 S275,24 300,16 S350,8 375,16 L400,16;
                    M0,16 C25,8 50,24 75,16 S125,8 150,16 S200,24 225,16 S275,8 300,16 S350,24 375,16 L400,16
                  "
                />
              </path>
            </svg>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                key={`spark-${chart.id}-${sparkData.length}-${lastValue}`}
                data={wavySparkData}
                margin={{ top: 10, right: 6, left: 6, bottom: 6 }}
              >
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={primary} stopOpacity={0.55} />
                    <stop offset="55%" stopColor={primary} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id={`${gradientId}-stroke`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={primary} stopOpacity={0.45} />
                    <stop offset="45%" stopColor={primary} stopOpacity={1} />
                    <stop offset="100%" stopColor={primary} stopOpacity={0.45} />
                    <animate
                      attributeName="x1"
                      values="-80%;100%;-80%"
                      dur="3.5s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="x2"
                      values="20%;200%;20%"
                      dur="3.5s"
                      repeatCount="indefinite"
                    />
                  </linearGradient>
                </defs>
                <YAxis hide domain={sparkYDomain} />
                <Area
                  type="natural"
                  dataKey="value"
                  stroke={`url(#${gradientId}-stroke)`}
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={false}
                  isAnimationActive
                  animationBegin={0}
                  animationDuration={1800}
                  animationEasing="ease-in-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          </KpiSparklineAnimated>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            Collecting trend data…
          </Typography>
        )}

        {lastTime && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Last updated · {lastTime}
          </Typography>
        )}
      </Box>
    );
  };

  const renderChart = (chart) => {
    const { chartType, dataPoints } = chart;
    const chartColors = COLORS(theme);

    if (chartType === 'KPIChart') {
      if (!dataPoints?.length && chart.currentValue == null) {
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 6, textAlign: 'center', fontStyle: 'italic' }}
          >
            No telemetry data for &quot;{chart.selectedInstrument}&quot; yet. Check that the gateway is connected and try Refresh.
          </Typography>
        );
      }
      return renderKPIChart(chart);
    }

    if (chartType === 'PieChart') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie
              data={mockPieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius="80%"
              fill={chartColors[0]}
              dataKey="value"
            >
              {mockPieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
              ))}
            </Pie>
            <RechartsTooltip />
            <Legend verticalAlign="bottom" height={36} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (!dataPoints || dataPoints.length === 0) {
      if (chart.currentValue == null) {
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 6, textAlign: 'center', fontStyle: 'italic' }}
          >
            No telemetry data for &quot;{chart.selectedInstrument}&quot; yet. Check that the gateway is connected and try Refresh.
          </Typography>
        );
      }
    }

    const rawSeries =
      dataPoints?.length > 0
        ? dataPoints
        : [{ time: 'Latest', value: chart.currentValue }];
    const seriesData =
      chartType === 'LineChart' || chartType === 'AreaChart'
        ? ensureChartSeriesData(rawSeries)
        : rawSeries;
    const yDomain = getYDomain(seriesData);
    const primaryChartColor = theme.palette.brand?.royalBlue || theme.palette.primary.main;
    const showDots = seriesData.length <= 2;

    switch (chartType) {
      case 'LineChart':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={seriesData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="time" stroke={theme.palette.text.secondary} />
              <YAxis domain={yDomain} stroke={theme.palette.text.secondary} />
              <RechartsTooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                stroke={primaryChartColor}
                strokeWidth={2}
                dot={showDots}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'BarChart':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={seriesData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="time" stroke={theme.palette.text.secondary} />
              <YAxis domain={yDomain} stroke={theme.palette.text.secondary} />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="value" fill={primaryChartColor} />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'AreaChart':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={seriesData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="time" stroke={theme.palette.text.secondary} />
              <YAxis domain={yDomain} stroke={theme.palette.text.secondary} />
              <RechartsTooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="value"
                stroke={primaryChartColor}
                fill={primaryChartColor}
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, padding: '20px', position: 'relative' }}>
      {(userRole === 'Admin' || userRole === 'Manager') && (
      <Fab
        color="primary"
        aria-label="add"
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16,
          backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
          color: (theme) => theme.palette.getContrastText(theme.palette.brand?.royalBlue || theme.palette.primary.main),
          '&:hover': {
            backgroundColor: (theme) => theme.palette.mode === 'dark' 
              ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
              : theme.palette.brand?.royalBlue || theme.palette.primary.main,
            filter: (theme) => theme.palette.mode === 'dark' ? 'none' : 'brightness(0.95)',
          },
        }}
        onClick={handleClickOpen}
      >
        <AddIcon />
      </Fab>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleClose}
      >
        <DialogTitle>Create New Chart</DialogTitle>
        <DialogContent>
          <TextField
            label="Chart Name"
            variant="outlined"
            fullWidth
            margin="normal"
            name="chartName"
            value={chartDetails.chartName}
            onChange={handleChange}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Instrument</InputLabel>
            <Select
              label="Instrument"
              name="selectedInstrument"
              value={chartDetails.selectedInstrument}
              onChange={handleChange}
            >
              {instrumentNames.length === 0 ? (
                <MenuItem disabled value="">
                  No instruments — add one in the Instruments tab first
                </MenuItem>
              ) : (
                instrumentNames.map((instrumentName) => (
                  <MenuItem key={instrumentName} value={instrumentName}>
                    {instrumentName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Chart Type</InputLabel>
            <Select
              label="Chart Type"
              name="chartType"
              value={chartDetails.chartType}
              onChange={handleChange}
            >
              {CHART_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="History Duration (hours)"
            name="hours"
            type="number"
            value={chartDetails.hours}
            onChange={handleChange}
            variant="outlined"
            margin="normal"
            inputProps={{ min: 1, max: 4320 }}
          />

          {saveError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {saveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={savingChart}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={savingChart || instrumentNames.length === 0}
          >
            {savingChart ? 'Creating...' : 'Create Chart'}
          </Button>
        </DialogActions>
      </Dialog>

      {loadingCharts ? (
        <Grid container spacing={3}>
          {[0, 1].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <ChartCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : charts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No charts yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the + button to create a KPI, line, bar, or area chart for your instruments.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {charts.map((chart) => (
            <Grid item xs={12} md={6} key={`${chart.id}-${chart.chartType}`}>
              <StyledCard>
                <ChartHeader>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WaterDropIcon sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary, fontWeight: 600 }}>
                      {chart.chartName}
                    </Typography>
                  </Box>
                  <Box>
                    <Tooltip title="Edit Chart">
                      <IconButton 
                        size="small" 
                        sx={{ 
                          mr: 1, 
                          color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main, 
                          '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) } 
                        }} 
                        onClick={() => handleEdit(chart)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh Data">
                      <IconButton 
                        size="small" 
                        sx={{ 
                          mr: 1, 
                          color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main, 
                          '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) } 
                        }} 
                        onClick={() => handleRefresh(chart)}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Chart Settings">
                      <IconButton 
                        size="small" 
                        sx={{ 
                          mr: 1, 
                          color: (theme) => theme.palette.text.secondary, 
                          '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.12 : 0.08) } 
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    { (userRole === 'Admin' || userRole === 'Manager') && (
                    <Tooltip title="Delete Chart">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteChart(chart.id)}
                        sx={{ 
                          color: (theme) => theme.palette.error.main, 
                          '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) } 
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    )}
                  </Box>
                </ChartHeader>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2 }}>
                  {shouldShowChartSkeleton(chart) ? (
                    <>
                      {chart.chartType !== 'KPIChart' && chart.chartType !== 'PieChart' && (
                        <Skeleton
                          variant="text"
                          width={140}
                          height={44}
                          sx={{ mb: 1, borderRadius: 1, alignSelf: 'center' }}
                        />
                      )}
                      <ChartDataSkeleton chartType={chart.chartType} />
                    </>
                  ) : (
                    <>
                      {chart.chartType !== 'KPIChart' && chart.currentValue != null && (
                        <Typography
                          variant="h4"
                          sx={{
                            mb: 1,
                            color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                            fontWeight: 600,
                          }}
                        >
                          {Number(chart.currentValue).toFixed(2)}
                        </Typography>
                      )}
                      {chart.isLiveOnly && chart.chartType !== 'KPIChart' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                          Live reading — history data building up over time
                        </Typography>
                      )}
                      <Box key={`${chart.id}-${chart.chartType}`} sx={{ width: '100%' }}>
                        {renderChart(chart)}
                      </Box>
                    </>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Chart</DialogTitle>
        <DialogContent>
          { (userRole === 'Admin' || userRole === 'Manager') && (
            <>
          <TextField fullWidth label="Chart Name" name="chartName" value={chartDetails.chartName} onChange={handleChange} variant="outlined" margin="normal" />
          <FormControl fullWidth margin="normal">
            <InputLabel>Instrument</InputLabel>
            <Select label="Instrument" name="selectedInstrument" value={chartDetails.selectedInstrument} onChange={handleChange}>
              {instrumentNames.map((tag) => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Chart Type</InputLabel>
            <Select label="Chart Type" name="chartType" value={chartDetails.chartType} onChange={handleChange}>
              {CHART_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
            </>
          )}
          <TextField fullWidth label="History Duration (hours)" name="hours" type="number" value={chartDetails.hours} onChange={handleChange} variant="outlined" margin="normal" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">Save</Button>
          <Button onClick={() => setOpenEditDialog(false)} color="secondary">Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisualizationTab;
